import { REEBELO_TZ_NAME } from '@lambda/reebelo';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import relativeTime from 'dayjs/plugin/relativeTime';
import { BadgeCheck } from 'lucide-react';
import { capitalize } from 'lodash-es';
import settings from '@/settings';
import {
  ReviewCardHeaderProps,
  ReviewCardOfferProps,
  ReviewCardProps,
} from './types';
import { Stars } from '../../main/right/product/ReviewsSummary';
import i18n from '@/settings/i18n';
import { formatStoreDate } from '@/lib/intl-format';

dayjs.extend(timezone);
dayjs.extend(relativeTime);

const t = i18n.init('product');

const storeTimeZone = REEBELO_TZ_NAME[settings.store];

const Header = ({ stars, customerName, createdAt }: ReviewCardHeaderProps) => {
  // EU stores: Intl's short date ("15/10/26", "15.10.26"). Elsewhere French
  // writes the day before the month (DD/MM/YY).
  const dateFormat = i18n.getLocale() === 'fr' ? 'DD/MM/YY' : 'MM/DD/YY';
  const createdAtDate = createdAt ? dayjs.tz(createdAt, storeTimeZone) : null;
  const formattedCreatedAt = createdAtDate
    ? formatStoreDate(createdAtDate, 'short') ??
      createdAtDate.format(dateFormat)
    : null;

  return (
    <div className="border-b pb-2">
      <div className="flex w-full flex-1 flex-col-reverse justify-between gap-2 xs:flex-row xs:items-center">
        <div className="flex items-center gap-x-2">
          <h3 className="text-sm font-bold">{customerName}</h3>
          <Stars stars={stars} className="h-3.5" />
        </div>
        <div className="flex h-5 w-fit items-center gap-1 rounded-full bg-[#D6FFE2] px-2 text-xxs font-medium text-[#128452]">
          <BadgeCheck className="h-3 w-3" strokeWidth={2.5} />
          {t`Verified Purchase`}
        </div>
      </div>
      {formattedCreatedAt && (
        <div className="shrink-0 text-gray-700/75">
          {t`Reviewed on ${{ date: formattedCreatedAt }}`}
        </div>
      )}
    </div>
  );
};

const Content = ({ text, title }: { text: string; title: string }) => (
  <div>
    <h4 className="font-bold">{title}</h4>
    <div className="mt-1 flex-1 leading-relaxed">{text}</div>
  </div>
);

const Offer = ({ offer }: ReviewCardOfferProps) => {
  const offerTitle = offer?.title ?? '';

  const offerVariants = [
    offer?.color,
    offer?.storage?.toUpperCase(),
    offer?.condition,
  ].filter(Boolean);

  return (
    <div>
      {offerTitle && <hr className="my-2" />}
      <h4 className="text-gray-700/75">
        {offerTitle
          .split(' ')
          .map((word) => (word === 'iphone' ? 'iPhone' : capitalize(word)))
          .join(' ')}{' '}
        <span className="capitalize">
          {offerVariants.length > 0 ? ` - ${offerVariants.join(' - ')}` : null}
        </span>
      </h4>
    </div>
  );
};

const ReviewCard = ({
  title,
  stars,
  text,
  createdAt,
  customerName,
  offer,
}: ReviewCardProps) => (
  <div className="flex h-full flex-col justify-between rounded border bg-white p-3 text-xs">
    <div className="flex flex-1 flex-col justify-between ">
      <div className="flex flex-1 flex-col gap-2">
        <Header
          stars={stars}
          customerName={customerName}
          createdAt={createdAt}
        />
        <Content text={text} title={title} />
      </div>
    </div>
    {offer && <Offer offer={offer} />}
  </div>
);

export default ReviewCard;
